// Türkçe biçim kuralları (04-copy-and-messaging.md):
// saat "07:42" (24 saat) · süre "6 dk" · mesafe "750 m" / "1,8 km" · sayı "1.284" · yüzde "%94" · plaka "34 SRT 145".

export const TIME_ZONE = 'Europe/Istanbul';

export function formatNumber(n: number, fractionDigits = 0): string {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/** "%94", "%4,1" — Türkçede yüzde işareti başta. */
export function formatPercent(value: number, fractionDigits = 0): string {
  return '%' + formatNumber(value, fractionDigits);
}

/** 750 → "750 m", 1840 → "1,8 km", 12400 → "12,4 km". */
export function formatDistance(meters: number): string {
  if (meters < 1000) return Math.max(0, Math.round(meters / 10) * 10) + ' m';
  const km = meters / 1000;
  return formatNumber(km, km >= 100 ? 0 : 1) + ' km';
}

/** ETA parçaları: ekranda "6" + "dk"; 1 dk altı "Şimdi". */
export function formatEta(minutes: number): { n: string; u: string } {
  if (minutes < 1) return { n: 'Şimdi', u: '' };
  return { n: String(Math.round(minutes)), u: 'dk' };
}

/** Ekran okuyucu metni: "6 dakika, tahmini 07:42". */
export function etaA11y(minutes: number, at?: Date | string | number): string {
  const base = minutes < 1 ? 'Şimdi' : Math.round(minutes) + ' dakika';
  return at ? base + ', tahmini ' + formatTime(at) : base;
}

/** "6 dk", "1 sa 5 dk". */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return m + ' dk';
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? h + ' sa ' + r + ' dk' : h + ' sa';
}

export function formatTime(date: Date | string | number): string {
  return new Intl.DateTimeFormat('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: TIME_ZONE,
  }).format(new Date(date));
}

// Saatin okunuşundaki son kelimeye göre ek: "15:40'ta" (kırk), "08:17'de" (yedi), "18:00'e kadar" (on sekiz).
const LOC: Record<number, string> = { 0: 'da', 1: 'de', 2: 'de', 3: 'te', 4: 'te', 5: 'te', 6: 'da', 7: 'de', 8: 'de', 9: 'da' };
const LOC_TENS: Record<number, string> = { 1: 'da', 2: 'de', 3: 'da', 4: 'ta', 5: 'de' };
const DAT: Record<number, string> = { 0: 'a', 1: 'e', 2: 'ye', 3: 'e', 4: 'e', 5: 'e', 6: 'ya', 7: 'ye', 8: 'e', 9: 'a' };
const DAT_TENS: Record<number, string> = { 1: 'a', 2: 'ye', 3: 'a', 4: 'a', 5: 'ye' };

function spokenSuffix(hhmm: string, ones: Record<number, string>, tens: Record<number, string>): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const n = m === 0 ? h : m;
  if (n === 0) return ones[0]!;
  return n % 10 ? ones[n % 10]! : tens[n / 10]!;
}

/** Bulunma eki ile saat: "15:40'ta", "08:17'de". */
export function timeAt(date: Date | string | number): string {
  const t = formatTime(date);
  return `${t}'${spokenSuffix(t, LOC, LOC_TENS)}`;
}

/** Yönelme eki ile saat: "18:00'e", "15:40'a" ("… kadar geçerli"). */
export function timeTo(date: Date | string | number): string {
  const t = formatTime(date);
  return `${t}'${spokenSuffix(t, DAT, DAT_TENS)}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toLocaleUpperCase('tr-TR') + s.slice(1);
}

/** "Salı, 14 Ekim" */
export function formatDayLong(date: Date | string | number): string {
  const d = new Date(date);
  const weekday = new Intl.DateTimeFormat('tr-TR', { weekday: 'long', timeZone: TIME_ZONE }).format(d);
  const day = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', timeZone: TIME_ZONE }).format(d);
  return capitalize(weekday) + ', ' + day;
}

/** "Pzt", "Sal" … */
export const WEEKDAYS_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const;
export const WEEKDAYS_LONG = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'] as const;
export const MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
] as const;

/** "14.10.2026" */
export function formatDate(date: Date | string | number): string {
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TIME_ZONE,
  }).format(new Date(date));
}

/** "14 Eki 2026" */
export function formatDateShort(date: Date | string | number): string {
  return new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: TIME_ZONE,
  }).format(new Date(date));
}

/** "14.10.2026 07:42" */
export function formatDateTime(date: Date | string | number): string {
  return formatDate(date) + ' ' + formatTime(date);
}

/** "2026-10" → "Ekim 2026" */
export function formatMonth(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return (MONTHS[(m ?? 1) - 1] ?? '') + ' ' + y;
}

/** Kuruş → "₺12.450,00" */
export function formatMoney(kurus: number, withDecimals = true): string {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: withDecimals ? 2 : 0,
  }).format(kurus / 100);
}

const PLATE_LETTERS = 'A-ZÇĞİÖŞÜ';

/** Ham plaka girişi → "34 ABC 123" (büyük harf, boşluklu). */
export function formatPlate(raw: string): string {
  const s = raw.toLocaleUpperCase('tr-TR').replace(new RegExp('[^0-9' + PLATE_LETTERS + ']', 'g'), '');
  const city = s.match(/^\d{1,2}/)?.[0] ?? '';
  const rest = s.slice(city.length);
  const letters = rest.match(new RegExp('^[' + PLATE_LETTERS + ']*'))?.[0] ?? '';
  const digits = rest.slice(letters.length).replace(/\D/g, '');
  return [city, letters, digits].filter(Boolean).join(' ');
}

export function isValidPlate(raw: string): boolean {
  return new RegExp('^(0[1-9]|[1-7]\\d|8[01]) [' + PLATE_LETTERS + ']{1,3} \\d{2,4}$').test(formatPlate(raw));
}

/** "+90 532 418 27 90" */
export function formatPhone(e164OrLocal: string): string {
  const d = e164OrLocal.replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
  const parts = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 8), d.slice(8, 10)].filter(Boolean);
  return ('+90 ' + parts.join(' ')).trim();
}

/** Kullanıcı girişini E.164'e çevirir: "0532 418 27 90" → "+905324182790". */
export function toE164(input: string): string | null {
  const d = input.replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
  return /^5\d{9}$/.test(d) ? '+90' + d : null;
}

/** Maskeli telefon: "+90 5•• ••• •• 90" (KVKK). */
export function maskPhone(e164: string): string {
  const d = e164.replace(/\D/g, '').replace(/^90/, '');
  return '+90 ' + d.slice(0, 1) + '•• ••• •• ' + d.slice(-2);
}

export function initials(first?: string | null, last?: string | null): string {
  const s = (first ?? '').charAt(0) + (last ?? '').charAt(0);
  return s.toLocaleUpperCase('tr-TR') || '?';
}

export function fullName(p: { firstName?: string | null; lastName?: string | null }): string {
  return [p.firstName, p.lastName].filter(Boolean).join(' ');
}

/** "3 sn önce", "4 dk önce", "Dün" */
export function formatRelative(date: Date | string | number, now: Date = new Date()): string {
  const diffS = Math.round((now.getTime() - new Date(date).getTime()) / 1000);
  if (diffS < 60) return Math.max(1, diffS) + ' sn önce';
  const m = Math.round(diffS / 60);
  if (m < 60) return m + ' dk önce';
  const h = Math.round(m / 60);
  if (h < 24) return h + ' sa önce';
  const d = Math.round(h / 24);
  return d === 1 ? 'Dün' : d + ' gün önce';
}

/** Belirtme hâli: "Elisa'yı", "Mert'i", "Umut'u", "Gül'ü". */
export function accusative(name: string): string {
  const vowels = 'aeıioöuüAEIİOÖUÜ';
  const lastVowel = [...name].reverse().find((ch) => vowels.includes(ch)) ?? 'e';
  const back = 'aıouAIOU'.includes(lastVowel);
  const rounded = 'oöuüOÖUÜ'.includes(lastVowel);
  const v = rounded ? (back ? 'u' : 'ü') : back ? 'ı' : 'i';
  return name + "'" + (vowels.includes(name.slice(-1)) ? 'y' : '') + v;
}

/** Türkçe çekim eki yardımcıları: "Elisa'nın", "Mert'in". */
export function possessive(name: string): string {
  const vowels = 'aeıioöuüAEIİOÖUÜ';
  const lastVowel = [...name].reverse().find((ch) => vowels.includes(ch)) ?? 'e';
  const back = 'aıouAIOU'.includes(lastVowel);
  const rounded = 'oöuüOÖUÜ'.includes(lastVowel);
  const v = rounded ? (back ? 'u' : 'ü') : back ? 'ı' : 'i';
  const endsWithVowel = vowels.includes(name.slice(-1));
  return name + "'" + (endsWithVowel ? 'n' : '') + v + 'n';
}
